import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select } from "@/components/ui/select";
import { Notify } from "@/components/ui/notify";
import type {
  ImportPreviewResponse,
  ImportColumnMapping,
  MappingDraft,
} from "@/types/data-import.types";
import { useBatchCharacteristics } from "@/features/organization/batch-characteristics/hooks";
import { useLineChains } from "@/features/lines/chain/hooks";
import { SheetPicker } from "../mapping/SheetPicker";
import { ColumnTargetRow, type LineOption, type ProductOption } from "../mapping/ColumnTargetRow";
import { CharacteristicColumnRow } from "../mapping/CharacteristicColumnRow";
import { OriginRulesField } from "../mapping/OriginRulesField";
import { TranslatedPreview } from "../mapping/TranslatedPreview";
import { directPairsOf } from "../mapping/origin-pairs";

interface MappingStepProps {
  preview: ImportPreviewResponse;
  draft: MappingDraft;
  setDraft: (patch: Partial<MappingDraft>) => void;
  lines: LineOption[];
  products: ProductOption[];
  profileName: string;
  setProfileName: (name: string) => void;
  isPending: boolean;
  onCancel: () => void;
  onSaveMapping: () => void;
  onExecute: () => void;
}

/**
 * Spec 082 — o gestor ensina a plataforma a ler a planilha da fábrica: cada coluna aponta para uma
 * linha, um produto e um sentido, ou é explicitamente ignorada. Coluna sem decisão bloqueia a
 * execução, porque ignorar é uma escolha e precisa ser declarada.
 */
export function MappingStep({
  preview,
  draft,
  setDraft,
  lines,
  products,
  profileName,
  setProfileName,
  isPending,
  onCancel,
  onSaveMapping,
  onExecute,
}: MappingStepProps) {
  const characteristicsQuery = useBatchCharacteristics();
  const chainsQuery = useLineChains();
  const characteristics = characteristicsQuery.data ?? [];
  const chains = chainsQuery.data?.items ?? [];
  // Falha de leitura NÃO vira ausência: sem esta guarda, a cadeia que não carregou faz a tela
  // AFIRMAR "nenhuma etapa deste arquivo alimenta outra na cadeia", e o catálogo que não carregou
  // esconde a decisão "Marcar a batelada" — os dois parecem configuração faltando, não erro.
  const leituraFalhou = characteristicsQuery.isError || chainsQuery.isError;
  const pending = preview.unmappedColumns.length;
  // Spec 084 — grafia sem resposta também bloqueia: a rota recusa, e oferecer o botão faria a tela
  // prometer uma execução que não vai acontecer.
  const grafiasPendentes = preview.characteristicColumns.reduce(
    (acc, c) => acc + c.pendingCount,
    0
  );
  const canExecute =
    pending === 0 && grafiasPendentes === 0 && draft.dateColumn !== null && !isPending;
  const paresDaCadeia = directPairsOf(chains, draft.columnMapping);

  function setColumn(column: string, decision: ImportColumnMapping) {
    setDraft({ columnMapping: { ...draft.columnMapping, [column]: decision } });
  }

  return (
    <div className="space-y-4">
      {preview.suggestedProfile ? (
        <Notify.Banner
          variant="info"
          title={`Mapeamento reaproveitado de "${preview.suggestedProfile.name}"`}
          description="Confira as colunas antes de executar — o arquivo pode ter mudado desde a última vez."
        />
      ) : null}

      <SheetPicker
        sheets={preview.sheets}
        sheetName={draft.sheetName}
        headerRow={draft.headerRow}
        onSheetChange={(sheetName) => setDraft({ sheetName })}
        onHeaderRowChange={(headerRow) => setDraft({ headerRow })}
      />

      <div className="space-y-1.5">
        <Label htmlFor="import-date-column">Coluna do dia</Label>
        <Select
          id="import-date-column"
          value={draft.dateColumn ?? ""}
          onChange={(e) => setDraft({ dateColumn: e.target.value || null })}
        >
          <option value="">Escolha a coluna com a data de produção</option>
          {preview.headers.map((h) => (
            <option key={h} value={h}>
              {h}
            </option>
          ))}
        </Select>
      </div>

      <div>
        <div className="mb-1.5 flex items-baseline justify-between gap-2">
          <h3 className="text-[14px] font-medium text-text-primary">O que cada coluna registra</h3>
          <span className="text-[12px] text-text-secondary">
            {pending === 0 ? "Todas as colunas decididas" : `${pending} sem decisão`}
            {grafiasPendentes > 0 ? ` · ${grafiasPendentes} grafia(s) sem resposta` : ""}
          </span>
        </div>
        <div className="rounded-md border border-border-default">
          {preview.headers.map((h, idx) => (
            <ColumnTargetRow
              key={h}
              column={h}
              sample={preview.rows[0]?.[idx] ?? ""}
              decision={draft.columnMapping[h]}
              lines={lines}
              products={products}
              onChange={(decision) => setColumn(h, decision)}
              characteristicAvailable={characteristics.length > 0}
              characteristicSlot={
                draft.columnMapping[h]?.action === "CHARACTERISTIC" ? (
                  <CharacteristicColumnRow
                    column={h}
                    decision={draft.columnMapping[h]}
                    characteristics={characteristics}
                    preview={preview.characteristicColumns.find((c) => c.column === h)}
                    onChange={(decision) => setColumn(h, decision)}
                  />
                ) : null
              }
            />
          ))}
        </div>
      </div>

      {leituraFalhou ? (
        <Notify.QueryError
          error={characteristicsQuery.error ?? chainsQuery.error}
          onRetry={() => {
            void characteristicsQuery.refetch();
            void chainsQuery.refetch();
          }}
          variant="inline"
        />
      ) : null}

      <div className="space-y-1.5">
        <Label htmlFor="import-origin-rules">De onde veio o material de cada etapa</Label>
        <div id="import-origin-rules">
          <OriginRulesField
            rules={draft.originRules}
            pairs={paresDaCadeia}
            lines={lines}
            onChange={(originRules) => setDraft({ originRules })}
          />
        </div>
      </div>

      <div className="grid gap-3 md:grid-cols-2">
        <div className="space-y-1.5">
          <Label htmlFor="import-conflict">Se o dia já tiver produção registrada</Label>
          <Select
            id="import-conflict"
            value={draft.conflictPolicy}
            onChange={(e) =>
              setDraft({ conflictPolicy: e.target.value as MappingDraft["conflictPolicy"] })
            }
          >
            <option value="IGNORE">Pular esse dia</option>
            <option value="REPLACE">Substituir os valores</option>
            <option value="FAIL">Recusar a importação inteira</option>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="import-profile-name">Guardar este mapeamento como</Label>
          <Input
            id="import-profile-name"
            placeholder="Controle de produção"
            value={profileName}
            onChange={(e) => setProfileName(e.target.value)}
          />
          <p className="text-[12px] text-text-secondary">
            Opcional. O próximo arquivo com estas colunas já vem mapeado.
          </p>
        </div>
      </div>

      <div className="space-y-2">
        <h3 className="text-[14px] font-medium text-text-primary">O que vai entrar na base</h3>
        <TranslatedPreview
          rows={preview.translated}
          ignoredColumns={preview.ignoredColumns}
          originPreview={preview.originPreview}
          suspiciousDays={preview.suspiciousDays}
        />
      </div>

      <div className="flex flex-wrap justify-between gap-2">
        <Button variant="outline" onClick={onCancel}>
          Cancelar
        </Button>
        <div className="flex gap-2">
          <Button variant="outline" onClick={onSaveMapping} disabled={isPending}>
            Conferir mapeamento
          </Button>
          <Button onClick={onExecute} disabled={!canExecute}>
            Executar importação
          </Button>
        </div>
      </div>
    </div>
  );
}
